import React, { useState, useEffect } from 'react';
import { Copy, Check, ExternalLink, BarChart3, Code, Eye, Settings } from 'lucide-react';
import { analyticsService, AnalyticsConfig } from '../../lib/analytics';
import { useBusiness } from '../../contexts/BusinessContext';

interface AnalyticsSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AnalyticsSettings({ isOpen, onClose }: AnalyticsSettingsProps) {
  const { businessId } = useBusiness();
  const [config, setConfig] = useState<AnalyticsConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'setup' | 'preview'>('setup');

  useEffect(() => {
    if (isOpen && businessId) {
      loadConfig();
    }
  }, [isOpen, businessId]);

  const loadConfig = async () => {
    if (!businessId) return;
    
    setIsLoading(true);
    try {
      const analyticsConfig = await analyticsService.getConfig(businessId);
      setConfig(analyticsConfig);
    } catch (error) {
      console.error('Error loading analytics config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!businessId) return;
    
    setIsSaving(true);
    try {
      const trackingCode = analyticsService.generateTrackingCode(businessId);
      const newConfig = await analyticsService.saveConfig({
        business_id: businessId,
        provider: 'custom',
        is_active: true,
        tracking_code: trackingCode
      });
      
      if (newConfig) {
        setConfig(newConfig);
      }
    } catch (error) {
      console.error('Error activating analytics:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyCode = async () => {
    if (!config?.tracking_code) return;
    
    try {
      await navigator.clipboard.writeText(config.tracking_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying code:', error);
    }
  };

  const handleDeactivate = async () => {
    if (!businessId || !config) return;
    
    setIsSaving(true);
    try {
      const updatedConfig = await analyticsService.saveConfig({
        ...config,
        is_active: false
      });
      
      if (updatedConfig) {
        setConfig(updatedConfig);
      }
    } catch (error) {
      console.error('Error deactivating analytics:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100020] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header with Mobile-Optimized Design */}
        <div className="relative bg-gray-800 border-b border-gray-700 shadow-lg">
          <div className="flex items-center justify-between px-2 py-2 sm:px-6 sm:py-4">
            <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shadow-sm">
                <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-white truncate">
                  Nexus Analytics
                </h2>
                <div className="text-xs sm:text-sm text-gray-300 font-medium">
                  Track website visits and user behavior
                </div>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
              style={{ minWidth: '32px', minHeight: '32px' }}
            >
              <svg className="h-4 w-4 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs with Glassmorphism Design */}
        <div className="bg-gray-800/95 backdrop-blur-xl border-b border-gray-700/50"
             style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
          <div className="max-w-5xl mx-auto px-2 sm:px-6">
            <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto" aria-label="Tabs" style={{ WebkitOverflowScrolling: 'touch' }}>
              <button
                onClick={() => setActiveTab('setup')}
                className={`py-3 px-2 sm:px-0 border-b-2 text-sm font-bold whitespace-nowrap transition-all duration-300 ease-out touch-manipulation ${
                  activeTab === 'setup'
                    ? 'border-purple-400 text-white'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }`}
                style={{ minHeight: '44px' }}
              >
                <div className="flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>Setup</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`py-3 px-2 sm:px-0 border-b-2 text-sm font-bold whitespace-nowrap transition-all duration-300 ease-out touch-manipulation ${
                  activeTab === 'preview'
                    ? 'border-purple-400 text-white'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }`}
                style={{ minHeight: '44px' }}
              >
                <div className="flex items-center space-x-2">
                  <Code className="h-4 w-4" />
                  <span>Tracking Code</span>
                </div>
              </button>

            </nav>
          </div>
        </div>

        {/* Content with mobile-optimized styling */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              {/* Setup Tab */}
              {activeTab === 'setup' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200/30 rounded-xl p-4 sm:p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-purple-900 mb-3">How it works</h3>
                    <p className="text-purple-800 text-sm leading-relaxed">
                      Add our tracking code to your website to monitor visitor behavior, page views, 
                      form submissions, and more. The code is lightweight and privacy-focused.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-gray-900 mb-1">Analytics Status</h4>
                          <p className="text-sm text-gray-600">
                            {config?.is_active 
                              ? 'Analytics is active and collecting data' 
                              : 'Analytics is not active'
                            }
                          </p>
                        </div>
                        <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                          config?.is_active 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                          {config?.is_active ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                    </div>

                    {!config?.is_active ? (
                      <button
                        onClick={handleActivate}
                        disabled={isSaving}
                        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-6 rounded-xl hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {isSaving ? 'Activating...' : 'Activate Analytics'}
                      </button>
                    ) : (
                      <button
                        onClick={handleDeactivate}
                        disabled={isSaving}
                        className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-4 px-6 rounded-xl hover:from-gray-700 hover:to-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {isSaving ? 'Deactivating...' : 'Deactivate Analytics'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Tracking Code Tab */}
              {activeTab === 'preview' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/30 rounded-xl p-4 sm:p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-blue-900 mb-3">Add to your website</h3>
                    <p className="text-blue-800 text-sm leading-relaxed mb-4">
                      Copy this tracking code and add it to your website's &lt;head&gt; section. 
                      For Webflow sites, go to Site Settings → Custom Code → Head Code.
                    </p>
                    
                    <div className="flex items-center space-x-2">
                      <a
                        href="https://webflow.com/help/article/add-custom-code-to-your-site"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium hover:underline transition-colors"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Webflow Instructions
                      </a>
                    </div>
                  </div>

                  {config?.tracking_code ? (
                    <div className="space-y-6">
                      <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm text-center">
                        <div className="mb-6">
                          <Code className="h-16 w-16 text-purple-600 mx-auto mb-4" />
                          <h4 className="text-xl font-bold text-gray-900 mb-2">Tracking Code Ready</h4>
                          <p className="text-gray-600 text-sm">
                            Your tracking code has been generated and is ready to be added to your website.
                          </p>
                        </div>
                        
                        <button
                          onClick={handleCopyCode}
                          className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-8 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                          {copied ? (
                            <>
                              <Check className="h-5 w-5" />
                              <span>Copied to Clipboard!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-5 w-5" />
                              <span>Copy Tracking Code</span>
                            </>
                          )}
                        </button>
                        
                        <div className="mt-4 text-xs text-gray-500">
                          Click to copy the complete tracking code to your clipboard
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-8">
                        <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 font-medium">Activate analytics to generate tracking code</p>
                      </div>
                    </div>
                  )}
                </div>
              )}


            </>
          )}
        </div>
      </div>
    </div>
  );
}
